'use client';

import {useEffect, useRef, useState, type RefObject} from 'react';

import type {LinkProps} from '../typings';
import type {MobileNavigationProps as Props} from './typings';

import Icon from '@/atoms/Icon/Icon';
import constantsFactory from '@/utils/constants';
import MobileSubMenuList from './MobileSubMenuList';
import MobileTopNavigation from './MobileTopNavigation';
import SideMenuDrawer from './SideMenuDrawer';
import SideMenuUserOptions from './SideMenuUserOptions';
import {
  StyledCloseButton,
  StyledFirstLevelMenu,
  StyledGoBackButton,
  StyledSecondLevelContent,
  StyledSecondLevelMenu,
  StyledSideMenuButton,
  StyledSideMenuControls,
  StyledSideMenuList,
  StyledSideMenuListItem,
} from './styles';

const {DATA_TEST_ID} = constantsFactory();
const slideAnimationDuration = 200;
const wait = (timeout: number) => new Promise((resolve) => setTimeout(resolve, timeout));
export const SIDE_NAV_ARIA = {
  FIRST_LEVEL: {
    ACTIVE: 'Side navigation menu active',
    INERT: 'Side navigation menu inert',
  },
  SECOND_LEVEL: {
    ACTIVE: 'Side navigation submenu active',
    INERT: 'Side navigation submenu inert',
  },
} as const;
const focusableSelectors = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
];

/**
 * @description Get all focusable elements inside the container div
 * @param {HTMLDivElement} container container div
 * @return {HTMLElement[]} array of focusable html elements
 */
const getFocusableElements = (container: HTMLDivElement): HTMLElement[] => {
  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors.join(','))).filter(
    (element) => !element.hasAttribute('disabled') && !element.getAttribute('aria-hidden'),
  );
};

/**
 * @description Get the top-level links from the shared navigation
 * @param {Array} linkProps shared navigation links
 * @return {Array} top-level links with their text, component, link, and submenu
 */
export const getTopLevelLinkLabels = (
  linkProps: Props['sharedNavigation'],
): (Pick<LinkProps, 'text' | 'component' | 'link'> & {hasSubMenu: boolean})[] =>
  linkProps.map(({text, component, link, submenu}) => ({
    text,
    component,
    link,
    hasSubMenu: !!submenu,
  }));

/**
 * @description calc the optional margin-top for the submenu UL list
 * @param {object} param
 * @param {number} param.index index of the submenu item
 * @param {string?} param.title title of the submenu item
 * @return {number | undefined} marginTop
 */
export const getListMarginTop = ({index, title}: {index: number; title?: string}) => {
  let marginTop: number | undefined = undefined;
  if (index > 0) {
    marginTop = -18;
  }
  if (!title) {
    marginTop = 34;
  }
  return marginTop;
};

/**
 * @description custom hook to track the previous value of a state
 * @param {T} value state value to be tracked
 * @return {T | undefined} previous state value
 */
const usePreviousState = <T = unknown,>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

/**
 * @description hook to trap focus inside the open side menu, when the menu is open
 * - focus the first focusable element
 * - cycle/trap focus on `'Tab'` key press, so the focus doesn't leave the open menu
 * - close the menu on `'Escape'` key press
 * @param {object} params
 * @param {RefObject<HTMLDivElement>} params.activeMenuRef ref to the active menu
 * @param {Function} params.onClose function to close the menu
 * @return {void} void/effect-only hook
 */
const useFocusTrap = ({
  activeMenuRef,
  onClose,
}: {
  activeMenuRef: RefObject<HTMLDivElement> | null;
  onClose: () => void;
}) => {
  useEffect(() => {
    if (!activeMenuRef?.current) return;
    const container = activeMenuRef.current;

    const focusableElements = getFocusableElements(container);

    /**
     * Focus the first focusable element or the container itself
     * Add a small delay to ensure rendering is complete (especially for Safari)
     */
    setTimeout(() => {
      if (focusableElements.length) {
        focusableElements[0].focus();
      } else {
        container.focus();
      }
    }, slideAnimationDuration + 100);

    /**
     * @description Handle keydown events
     * - Close the menu on `'Escape'` key press
     * - Cycle/trap focus on `'Tab'` key press
     * @param {KeyboardEvent} event keyboard event
     * @return {void} void/procedure
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      if (!focusableElements.length) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    /**
     * @description Cleanup function to remove the event listener
     * and prevent memory leaks on unmount or when dependency changes
     */
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeMenuRef]);
};

/**
 * @description Mobile navigation component, that consists of
 * - top navigation bar
 * - side menu top-level nav inside the drawer
 * - side menu second-level nav inside the drawer
 * @param {object} props props that contain shared navigation tree
 * @return {JSX.Element} Mobile navigation markup
 */
const MobileNavigation = ({sharedNavigation, auth, userSettings}: Props): JSX.Element => {
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const wasPreviouslyOpen = usePreviousState(isSideMenuOpen);
  const [selectedSubMenu, setSelectedSubMenu] = useState<null | LinkProps['submenu']>(null);
  const mountedRef = useRef(true);
  const topLevelRef = useRef<HTMLDivElement>(null);
  const secondLevelRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const topLevelLinkLabels = getTopLevelLinkLabels(sharedNavigation);
  const isSideMenuTopLevelActive = isSideMenuOpen && !selectedSubMenu;
  const isSideMenuSecondLevelActive = isSideMenuOpen && !!selectedSubMenu;
  const isMenuButtonFocused = wasPreviouslyOpen === true && !isSideMenuOpen;

  const handleSelectSubMenu = (topLevelLink: string) => {
    const submenu = sharedNavigation.find(({link}) => link === topLevelLink)?.submenu || null;
    if (mountedRef.current) {
      setSelectedSubMenu(submenu);
    }
  };

  const handleCloseSubMenu = async () => {
    await wait(slideAnimationDuration);
    if (mountedRef.current) {
      setSelectedSubMenu(null);
    }
  };

  const handleCloseSideMenu = () => {
    setIsSideMenuOpen(false);
    setSelectedSubMenu(null);
  };

  useFocusTrap({
    activeMenuRef: isSideMenuTopLevelActive
      ? topLevelRef
      : isSideMenuSecondLevelActive
      ? secondLevelRef
      : null,
    onClose: handleCloseSideMenu,
  });

  /**
   * @description restore focus on button when the menu closes
   */
  useEffect(() => {
    if (isMenuButtonFocused) {
      menuButtonRef.current?.focus();
    }
  }, [isMenuButtonFocused]);

  /**
   * @description track the component mount status
   * to prevent set state updates on unmounted component
   */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <>
      <MobileTopNavigation
        auth={auth}
        userSettings={userSettings}
        handleOpenSideMenu={() => {
          setIsSideMenuOpen(true);
        }}
        ref={menuButtonRef}
      />
      <SideMenuDrawer isOpen={isSideMenuOpen} onClose={handleCloseSideMenu}>
        <StyledFirstLevelMenu
          aria-label={
            isSideMenuTopLevelActive
              ? SIDE_NAV_ARIA.FIRST_LEVEL.ACTIVE
              : SIDE_NAV_ARIA.FIRST_LEVEL.INERT
          }
          data-testid={DATA_TEST_ID.SIDE_NAVIGATION}
          ref={topLevelRef}
        >
          <StyledSideMenuControls>
            <StyledCloseButton
              tabIndex={isSideMenuTopLevelActive ? 0 : -1}
              onClick={handleCloseSideMenu}
              aria-label="Close navigation"
            >
              <Icon type="plus" fontSize={24} label="Close" />
            </StyledCloseButton>
          </StyledSideMenuControls>
          <StyledSideMenuList>
            {topLevelLinkLabels.map(({text, component, link, hasSubMenu}) => (
              <StyledSideMenuListItem key={link}>
                {hasSubMenu ? (
                  <StyledSideMenuButton
                    tabIndex={isSideMenuTopLevelActive ? 0 : -1}
                    onClick={() => {
                      link && handleSelectSubMenu(link);
                    }}
                  >
                    {text || component}
                  </StyledSideMenuButton>
                ) : (
                  <a
                    tabIndex={isSideMenuTopLevelActive ? 0 : -1}
                    href={link as string}
                    onClick={handleCloseSideMenu}
                  >
                    {text || component}
                  </a>
                )}
              </StyledSideMenuListItem>
            ))}
          </StyledSideMenuList>
          <SideMenuUserOptions
            auth={auth}
            handleCloseSideMenu={handleCloseSideMenu}
            isActive={isSideMenuTopLevelActive}
          />
        </StyledFirstLevelMenu>

        <StyledSecondLevelMenu
          data-testid={DATA_TEST_ID.SIDE_NAVIGATION_SUBMENU}
          aria-label={
            isSideMenuSecondLevelActive
              ? SIDE_NAV_ARIA.SECOND_LEVEL.ACTIVE
              : SIDE_NAV_ARIA.SECOND_LEVEL.INERT
          }
          $isOpen={!!selectedSubMenu}
          $animationDuration={slideAnimationDuration}
          ref={secondLevelRef}
        >
          <StyledSideMenuControls $isSubMenu>
            <StyledGoBackButton
              aria-hidden={!isSideMenuSecondLevelActive}
              disabled={!isSideMenuSecondLevelActive}
              onClick={handleCloseSubMenu}
              aria-label="Back to main menu"
              tabIndex={isSideMenuSecondLevelActive ? 0 : -1}
            >
              <Icon type="arrow-left" fontSize={16} label="Back" />
            </StyledGoBackButton>

            <StyledCloseButton
              aria-hidden={!isSideMenuSecondLevelActive}
              disabled={!isSideMenuSecondLevelActive}
              onClick={handleCloseSideMenu}
              aria-label="Close navigation"
              tabIndex={isSideMenuSecondLevelActive ? 0 : -1}
            >
              <Icon type="plus" fontSize={24} label="Close" />
            </StyledCloseButton>
          </StyledSideMenuControls>

          <StyledSecondLevelContent>
            {selectedSubMenu &&
              selectedSubMenu.map((item, index) => {
                return (
                  <MobileSubMenuList
                    key={index}
                    submenu={item}
                    handleItemClick={handleCloseSideMenu}
                    marginTop={getListMarginTop({index, title: item.title})}
                    isActive={isSideMenuSecondLevelActive}
                  />
                );
              })}
          </StyledSecondLevelContent>
        </StyledSecondLevelMenu>
      </SideMenuDrawer>
    </>
  );
};

export default MobileNavigation;
